import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import './VariationTemplates.css';

const defaultVariation = () => ({
  name: '',
  type: 'single',
  required: false,
  minSelections: 0,
  maxSelections: 1,
  options: []
});

const VariationTemplates = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState('');
  const [formVariations, setFormVariations] = useState([]);
  const [saving, setSaving] = useState(false);

  const getApiBase = () => `${API_URL}/admin`;

  useEffect(() => {
    fetchTemplates();
  }, []);

  const getErrorMessage = (err, fallback) => {
    if (!err) return fallback;
    const msg = err.response?.data?.message;
    if (msg && typeof msg === 'string') return msg;
    const status = err.response?.status;
    if (status === 401) return 'Please log in again.';
    if (status === 403) return 'Access denied.';
    if (status === 404) return 'Not found.';
    if (status != null) return `${fallback} (${status})`;
    return err.message || fallback;
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${getApiBase()}/variation-templates`, {
        headers: { Accept: 'application/json' },
      });
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching variation templates:', err?.response?.data || err);
      showError(getErrorMessage(err, 'Failed to load templates'));
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormName('');
    setFormVariations([defaultVariation()]);
  };

  const openEdit = (template) => {
    setEditing(template);
    setFormName(template.name || '');
    setFormVariations(
      (template.variations && template.variations.length)
        ? template.variations.map((v) => ({
            name: v.name || '',
            type: v.type || 'single',
            required: !!v.required,
            minSelections: v.minSelections ?? 0,
            maxSelections: v.maxSelections ?? (v.type === 'multiple' ? 10 : 1),
            options: Array.isArray(v.options) ? v.options.map((o) => ({ name: o.name || '', price: Number(o.price) || 0 })) : []
          }))
        : [defaultVariation()]
    );
  };

  const addVariation = () => {
    setFormVariations((prev) => [...prev, defaultVariation()]);
  };

  const removeVariation = (index) => {
    setFormVariations((prev) => prev.filter((_, i) => i !== index));
  };

  const updateVariation = (index, field, value) => {
    setFormVariations((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addOption = (vIndex) => {
    setFormVariations((prev) => {
      const next = [...prev];
      next[vIndex] = { ...next[vIndex], options: [...(next[vIndex].options || []), { name: '', price: 0 }] };
      return next;
    });
  };

  const removeOption = (vIndex, oIndex) => {
    setFormVariations((prev) => {
      const next = [...prev];
      next[vIndex] = { ...next[vIndex], options: next[vIndex].options.filter((_, i) => i !== oIndex) };
      return next;
    });
  };

  const updateOption = (vIndex, oIndex, field, value) => {
    setFormVariations((prev) => {
      const next = [...prev];
      next[vIndex] = {
        ...next[vIndex],
        options: next[vIndex].options.map((opt, i) => (i === oIndex ? { ...opt, [field]: value } : opt))
      };
      return next;
    });
  };

  const handleSave = async () => {
    const name = (formName || '').trim();
    if (!name) {
      showError(t('admin.variationTemplateNameRequired') || 'Template name is required');
      return;
    }
    const variations = formVariations
      .filter((v) => v && (v.name || '').trim())
      .map((v) => ({
        name: (v.name || '').trim(),
        type: v.type || 'single',
        required: !!v.required,
        minSelections: typeof v.minSelections === 'number' ? v.minSelections : 0,
        maxSelections: typeof v.maxSelections === 'number' ? v.maxSelections : v.type === 'multiple' ? 10 : 1,
        options: (v.options || []).filter((o) => o && (o.name || '').trim()).map((o) => ({ name: (o.name || '').trim(), price: Number(o.price) || 0 }))
      }));
    if (variations.length === 0) {
      showError(t('admin.variationTemplateOneVariation') || 'Add at least one variation with a name');
      return;
    }
    setSaving(true);
    try {
      const payload = { name, variations };
      const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
      if (editing && editing._id) {
        await axios.patch(`${getApiBase()}/variation-templates/${editing._id}`, payload, { headers });
        showSuccess(t('admin.variationTemplateUpdated') || 'Template updated');
      } else {
        await axios.post(`${getApiBase()}/variation-templates`, payload, { headers });
        showSuccess(t('admin.variationTemplateCreated') || 'Template created');
      }
      fetchTemplates();
      setEditing(null);
      setFormName('');
      setFormVariations([]);
    } catch (err) {
      console.error('Error saving variation template:', err?.response?.data || err);
      showError(getErrorMessage(err, 'Failed to save template'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(t('admin.variationTemplateDeleteConfirm') || `Delete template "${template.name}"?`)) return;
    try {
      await axios.delete(`${getApiBase()}/variation-templates/${template._id}`);
      showSuccess(t('admin.variationTemplateDeleted') || 'Template deleted');
      fetchTemplates();
      if (editing && editing._id === template._id) {
        setEditing(null);
        setFormName('');
        setFormVariations([]);
      }
    } catch (err) {
      showError(getErrorMessage(err, 'Failed to delete template'));
    }
  };

  if (loading) {
    return <div className="variation-templates-page"><p>{t('admin.loading') || 'Loading...'}</p></div>;
  }

  return (
    <div className="variation-templates-page">
      <h1>{t('admin.variationTemplates') || 'Variation templates'}</h1>
      <p className="variation-templates-intro">
        {t('admin.variationTemplatesIntro') || 'Create reusable variation templates (e.g. Size, Add-ons) and apply them to products when editing. You can still remove or edit individual variations on each product.'}
      </p>
      <div className="variation-templates-actions">
        <button type="button" className="btn-add-template" onClick={openCreate}>
          <i className="fas fa-plus"></i> {t('admin.addVariationTemplate') || 'Add template'}
        </button>
      </div>
      <div className="variation-templates-list">
        {templates.length === 0 && !editing && (
          <p className="no-templates">{t('admin.noVariationTemplates') || 'No templates yet. Create one to use in product management.'}</p>
        )}
        {templates.map((template) => (
          <div key={template._id} className="template-card">
            <div className="template-card-body">
              <h3>{template.name}</h3>
              <p className="template-meta">
                {template.variations?.length || 0} {t('admin.variations') || 'variations'}
              </p>
            </div>
            <div className="template-card-actions">
              <button type="button" className="btn-edit" onClick={() => openEdit(template)}>
                <i className="fas fa-edit"></i> {t('admin.edit') || 'Edit'}
              </button>
              <button type="button" className="btn-delete" onClick={() => handleDelete(template)}>
                <i className="fas fa-trash"></i> {t('admin.delete') || 'Delete'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {(editing != null || formName || formVariations.length > 0) && (
        <div className="variation-template-modal-overlay" onClick={() => { setEditing(null); setFormName(''); setFormVariations([]); }}>
          <div className="variation-template-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editing && editing._id ? (t('admin.editVariationTemplate') || 'Edit template') : (t('admin.addVariationTemplate') || 'Add template')}</h2>
            <div className="form-group">
              <label>{t('admin.templateName') || 'Template name'}</label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder={t('admin.templateNamePlaceholder') || 'e.g. Size, Add-ons'}
              />
            </div>
            <div className="variations-editor">
              <div className="section-header">
                <h3>{t('admin.variations') || 'Variations'}</h3>
                <button type="button" className="btn-add" onClick={addVariation}>
                  <i className="fas fa-plus"></i> {t('admin.addVariation') || 'Add variation'}
                </button>
              </div>
              {formVariations.map((v, vIndex) => (
                <div key={vIndex} className="variation-block">
                  <div className="variation-block-header">
                    <input
                      type="text"
                      placeholder={t('admin.variationNamePlaceholder') || 'Variation name (e.g. Size, Toppings)'}
                      value={v.name}
                      onChange={(e) => updateVariation(vIndex, 'name', e.target.value)}
                    />
                    <button type="button" className="btn-remove" onClick={() => removeVariation(vIndex)}>
                      <i className="fas fa-trash"></i> {t('admin.remove') || 'Remove'}
                    </button>
                  </div>
                  <div className="variation-options-row">
                    <label>
                      <input type="radio" checked={v.type === 'single'} onChange={() => updateVariation(vIndex, 'type', 'single')} />
                      {t('admin.singleChoice') || 'Single'}
                    </label>
                    <label>
                      <input type="radio" checked={v.type === 'multiple'} onChange={() => updateVariation(vIndex, 'type', 'multiple')} />
                      {t('admin.multipleChoice') || 'Multiple'}
                    </label>
                    <label>
                      <input type="checkbox" checked={!!v.required} onChange={(e) => updateVariation(vIndex, 'required', e.target.checked)} />
                      {t('admin.required') || 'Required'}
                    </label>
                  </div>
                  <div className="options-list">
                    <button type="button" className="btn-add-option" onClick={() => addOption(vIndex)}>
                      <i className="fas fa-plus-circle"></i> {t('admin.addOption') || 'Add option'}
                    </button>
                    {(v.options || []).map((opt, oIndex) => (
                      <div key={oIndex} className="option-row">
                        <input
                          type="text"
                          placeholder={t('admin.optionName') || 'Option name'}
                          value={opt.name}
                          onChange={(e) => updateOption(vIndex, oIndex, 'name', e.target.value)}
                        />
                        <input
                          type="number"
                          step="any"
                          min="0"
                          placeholder="€"
                          value={opt.price}
                          onChange={(e) => updateOption(vIndex, oIndex, 'price', parseFloat(e.target.value) || 0)}
                        />
                        <button type="button" className="btn-remove" onClick={() => removeOption(vIndex, oIndex)}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => { setEditing(null); setFormName(''); setFormVariations([]); }}>
                {t('admin.cancel') || 'Cancel'}
              </button>
              <button type="button" className="btn-save" onClick={handleSave} disabled={saving}>
                {saving ? (t('admin.saving') || 'Saving...') : (t('admin.save') || 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariationTemplates;
